import { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Filler, Tooltip, Legend,
} from 'chart.js';
import { MetricsDashboard, type MetricsDashboardMetric } from '../components/arc/metrics-dashboard/metrics-dashboard';
import { ArcDialog, ArcHeaderPeriodSelect, ArcPage, ArcPageHeader, ArcTableFrame, ArcTabs } from '../components/arc/primitives/arc-primitives';
import { useFeather } from '../shell/useFeather';
import { useTabParam } from '../shell/useTabParam';
import { ServiceBar } from '../shell/ServiceBar';
import { getAffilmasterPrijmy, getAffilmasterPrijmyMonth, AffilmasterApiError, type IncomeSeries, type IncomeSeriesRow, type SettlementForecast } from '../api/affilmaster';
import './affilmaster-dashboard.css';
import './affilmaster-detail.css';
import './affilmaster-prijmy.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Filler, Tooltip, Legend);

const AM_PURPLE = '#5b3f96';
const AM_ORANGE = '#ff9500';
const MONTH_CS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
const MONTH_SHORT = ['led', 'úno', 'bře', 'dub', 'kvě', 'čvn', 'čvc', 'srp', 'zář', 'říj', 'lis', 'pro'];
const fmt = (v: number) => Number(v || 0).toLocaleString('cs-CZ', { maximumFractionDigits: 0 });
const fmtCZK = (v: number) => { const n = Number(v) || 0; return n ? `${n.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč` : '-'; };
const fmtEUR = (v: number) => { const n = Number(v) || 0; return n ? `${n.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} €` : '-'; };
const fmtMoney = (v: number, currency: string) => (currency === 'EUR' ? fmtEUR(v) : fmtCZK(v));
const ymLabel = (ym: string) => { if (!/^\d{4}-\d{2}$/.test(ym)) return ym; const [y, m] = ym.split('-'); return `${MONTH_CS[Number(m) - 1] ?? m} ${y ?? ''}`; };
const ymShort = (ym: string) => { const [y, m] = ym.split('-'); return `${MONTH_SHORT[Number(m) - 1] ?? m} ${(y ?? '').slice(2)}`; };
const pctOf = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)} %` : '0 %');

type PrijmyTab = 'zakladni' | 'rozpad' | 'vypis';
const PRIJMY_TABS: readonly PrijmyTab[] = ['zakladni', 'rozpad', 'vypis'];

export function AffilmasterPrijmyPage() {
  const [series, setSeries] = useState<IncomeSeries | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useTabParam<PrijmyTab>('tab', PRIJMY_TABS, 'zakladni');
  const [ym, setYm] = useState('');
  const [rozpadMonths, setRozpadMonths] = useState<12 | 24>(12);

  useEffect(() => {
    let cancelled = false;
    getAffilmasterPrijmy(24)
      .then((d) => { if (!cancelled) { setSeries(d); setErr(null); } })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení příjmů selhalo'); });
    return () => { cancelled = true; };
  }, []);

  useFeather([series, tab, ym]);

  const monthOpts = useMemo(() => (series ? [...series.rows].reverse().map((r) => r.period) : []), [series]);
  const selectedYm = ym || series?.ym || '';

  return (
    <ArcPage className="affms-dash affms-prijmy">
      <ArcPageHeader
        eyebrow="AFFILMASTER"
        title="Příjmy"
        description="Fakturace klientům a provize Affilmasteru z dealů"
        meta={series ? (
          <div className="am-prijmy-head-meta" aria-label="Kontext příjmů">
            <ArcHeaderPeriodSelect value={selectedYm} onChange={setYm} options={monthOpts.map((option) => ({ value: option, label: ymLabel(option) }))} />
            <span>1 € = {series.rate.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} Kč (pevný kurz)</span>
          </div>
        ) : null}
      />
      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <i data-feather="alert-circle" />
          <div><strong>Příjmy se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}
      {!err && !series && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}

      {series && (
        <>
          <div className="prototype-overview-toolbar">
            <ArcTabs
              label="Příjmy"
              value={tab}
              onChange={setTab}
              items={[
                { value: 'zakladni', label: 'Základní' },
                { value: 'rozpad', label: 'Rozpad' },
                { value: 'vypis', label: 'Výpis' },
              ]}
            />
          </div>

          {tab === 'zakladni' && <TabZakladni ym={selectedYm} series={series} />}
          {tab === 'rozpad' && <TabRozpad series={series} months={rozpadMonths} onMonthsChange={setRozpadMonths} />}
          {tab === 'vypis' && <TabVypis series={series} />}
        </>
      )}
    </ArcPage>
  );
}

function SummaryMetric({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="am-prijmy-summary-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
  );
}

function MarketHead({ mk, name, meta }: { mk: 'cz' | 'sk'; name: string; meta: string }) {
  return (
    <div className="am-prijmy-market-head">
      <span className={`market-flag market-flag-${mk}`}>{mk.toUpperCase()}</span>
      <div><strong>{name}</strong><span>{meta}</span></div>
    </div>
  );
}

function TabZakladni({ ym, series }: { ym: string; series: IncomeSeries }) {
  const [d, setD] = useState<SettlementForecast | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!ym) return;
    let cancelled = false;
    setD(null); setErr(null);
    getAffilmasterPrijmyMonth(ym)
      .then((r) => { if (!cancelled) setD(r); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení měsíce selhalo'); });
    return () => { cancelled = true; };
  }, [ym]);
  useFeather([d]);

  const last12 = series.rows.slice(-12);
  const trendChart = {
    labels: last12.map((r) => ymShort(r.period)),
    datasets: [
      { label: 'CZ', data: last12.map((r) => Math.round(r.cz.totalCzk)), backgroundColor: AM_PURPLE, borderRadius: 3 },
      { label: 'SK', data: last12.map((r) => Math.round(r.sk.totalCzk)), backgroundColor: AM_ORANGE, borderRadius: 3 },
    ],
  };
  const trendOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } } };

  if (err) return <div className="alert alert-danger">{err}</div>;
  if (!d) return <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>;

  const fees = d.fees.totalCzk;
  const fixed = d.fixed.totalCzk;
  const total = d.income.totalCzk;
  const clients = d.partners.filter((p) => p.totalCzk > 0).length;
  const doughnut = { labels: ['Fixní', 'Provize'], datasets: [{ data: [Math.round(fixed), Math.round(fees)], backgroundColor: [AM_PURPLE, AM_ORANGE], borderWidth: 0 }] };
  const doughnutOpts = { responsive: true, maintainAspectRatio: false, cutout: '68%', plugins: { legend: { position: 'bottom' as const } } };
  const fixedPctTxt = pctOf(fixed, total);
  const centerText = {
    id: 'centerText',
    afterDraw(chart: { ctx: CanvasRenderingContext2D; chartArea: { left: number; right: number; top: number; bottom: number } }) {
      const { ctx, chartArea } = chart;
      const cx = (chartArea.left + chartArea.right) / 2;
      const cy = (chartArea.top + chartArea.bottom) / 2 - 8;
      ctx.save();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = AM_PURPLE; ctx.font = '700 1.6rem system-ui, sans-serif';
      ctx.fillText(fixedPctTxt, cx, cy);
      ctx.fillStyle = '#9aa0a6'; ctx.font = '600 0.62rem system-ui, sans-serif';
      ctx.fillText('FIXNÍ', cx, cy + 22);
      ctx.restore();
    },
  };

  const mkCard = (mk: 'cz' | 'sk', name: string, m: SettlementForecast['markets']['cz']) => (
    <article className={`prototype-panel am-prijmy-market am-prijmy-market-${mk}`} key={mk}>
      <MarketHead mk={mk} name={name} meta={`${fmt(m.clients)} ${m.clients === 1 ? 'klient' : (m.clients >= 2 && m.clients <= 4 ? 'klienti' : 'klientů')}`} />
      <div className="am-prijmy-market-metrics">
        <div className="am-prijmy-market-total"><span>Celkem</span><strong>{fmtCZK(m.totalCzk)}</strong></div>
        <div><span>Fixní</span><strong>{fmtCZK(m.fixedCzk)}</strong></div>
        <div><span>Provize</span><strong>{fmtCZK(m.feesCzk)}</strong></div>
        <div><span>Období</span><strong>{ymLabel(d.ym)}</strong></div>
      </div>
    </article>
  );

  return (
    <div className="tab-content am-prijmy-dashboard">
      <article className="prototype-panel am-prijmy-summary">
        <div className="am-prijmy-summary-lead">
          <div className="am-prijmy-summary-kicker">
            <span>PŘÍJMY ZA {ymLabel(d.ym).toUpperCase()}</span>
            {d.ym === series.ym && <small><i aria-hidden="true" />Aktuální období</small>}
          </div>
          <strong>{fmtCZK(total)}</strong>
          <p>Součet fixní fakturace a průběžného odhadu provizí AM.</p>
        </div>
        <div className="am-prijmy-summary-metrics">
          <SummaryMetric label="Fixní fakturace" value={fmtCZK(fixed)} note={`${pctOf(fixed, total)} z příjmů`} />
          <SummaryMetric label="Provize AM" value={fmtCZK(fees)} note={`${pctOf(fees, total)} z příjmů`} />
          <SummaryMetric label="Klienti s příjmem" value={`${fmt(clients)} / ${fmt(d.partners.length)}`} note="aktivní v období" />
        </div>
      </article>

      <section className="am-prijmy-section" aria-labelledby="am-prijmy-markets-title">
        <div className="am-prijmy-section-head">
          <div><span>TRHY</span><h2 id="am-prijmy-markets-title">Rozdělení příjmů</h2></div>
          <p>Částky jsou sjednocené do Kč.</p>
        </div>
        <div className="am-prijmy-markets">
          {mkCard('cz', 'Český trh', d.markets.cz)}
          {mkCard('sk', 'Slovenský trh', d.markets.sk)}
        </div>
      </section>

      <section className="am-prijmy-section" aria-labelledby="am-prijmy-charts-title">
        <div className="am-prijmy-section-head">
          <div><span>VÝVOJ</span><h2 id="am-prijmy-charts-title">Příjmy a jejich skladba</h2></div>
          <p>Posledních 12 měsíců</p>
        </div>
        <div className="am-prijmy-charts">
          <article className="prototype-panel am-prijmy-chart-card am-prijmy-chart-trend">
            <header>
              <h3>Příjmy po měsících</h3>
              <div className="am-prijmy-chart-legend" aria-label="Legenda">
                <span><i className="is-cz" />CZ</span>
                <span><i className="is-sk" />SK</span>
              </div>
            </header>
            <div className="am-prijmy-chart-canvas"><Bar data={trendChart} options={trendOpts as never} /></div>
          </article>
          <article className="prototype-panel am-prijmy-chart-card am-prijmy-chart-mix">
            <header><h3>Fixní vs. provize</h3></header>
            <div className="am-prijmy-chart-canvas"><Doughnut data={doughnut} options={doughnutOpts as never} plugins={[centerText as never]} /></div>
          </article>
        </div>
      </section>

      <div className="am-prijmy-note">
        <i data-feather="info" />
        <span>Provize jsou odhad z dat PaP; u klientů účtovaných podle vlastního reportu (Allwyn, Betano, Synot, MerkurXtip) jde o orientační hodnotu. € přepočteno kurzem ČNB {d.rate.toLocaleString('cs-CZ', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} Kč.</span>
      </div>
    </div>
  );
}

function TabRozpad({ series, months, onMonthsChange }: { series: IncomeSeries; months: 12 | 24; onMonthsChange: (months: 12 | 24) => void }) {
  const rows = series.rows.slice(-months);
  const previousRows = series.rows.slice(Math.max(0, series.rows.length - months * 2), Math.max(0, series.rows.length - months));
  const hasComparison = previousRows.length === rows.length;
  const sum = (source: IncomeSeriesRow[], pick: (row: IncomeSeriesRow) => number) => source.reduce((total, row) => total + pick(row), 0);
  const change = (current: number, previous: number) => previous ? ((current - previous) / Math.abs(previous)) * 100 : null;
  const compactCZK = (value: number) => `${fmt(value / 1000)} tis.`;
  const metric = (key: string, label: string, pick: (row: IncomeSeriesRow) => number, average = false): MetricsDashboardMetric => {
    const currentTotal = sum(rows, pick);
    const previousTotal = hasComparison ? sum(previousRows, pick) : 0;
    const value = average && rows.length ? currentTotal / rows.length : currentTotal;
    const previousValue = average && previousRows.length ? previousTotal / previousRows.length : previousTotal;
    const delta = hasComparison ? change(value, previousValue) : null;
    return {
      key,
      label,
      value,
      delta,
      deltaLabel: delta == null ? `${rows.length} období` : `${Math.abs(delta).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} % vs. minulé`,
      deltaDirection: delta == null || delta === 0 ? 'neutral' : delta > 0 ? 'up' : 'down',
      points: rows.map((row, index) => ({
        label: ymShort(row.period),
        value: pick(row),
        previous: hasComparison ? pick(previousRows[index]!) : null,
      })),
      format: fmtCZK,
      axisFormat: compactCZK,
    };
  };
  const metrics: MetricsDashboardMetric[] = [
    metric('celkem', 'Celkem', (row) => row.totalCzk),
    metric('fixni', 'Fixní příjmy', (row) => row.fixedCzk),
    metric('provize', 'Provize AM', (row) => row.feesCzk),
    metric('prumer', 'Průměr / měsíc', (row) => row.totalCzk, true),
  ];
  const marketRows = (market: 'cz' | 'sk') => [
    { name: 'Celkem', value: sum(rows, (row) => row[market].totalCzk), format: fmtCZK },
    { name: 'Fixní příjmy', value: sum(rows, (row) => row[market].fixedCzk), format: fmtCZK },
    { name: 'Provize AM', value: sum(rows, (row) => row[market].feesCzk), format: fmtCZK },
  ];

  return (
    <div className="tab-content am-prijmy-dashboard am-prijmy-arc p1-arc-scope">
      <MetricsDashboard
        title="Skladba příjmů"
        subtitle={`Posledních ${rows.length} měsíců · částky sjednocené do Kč`}
        metrics={metrics}
        defaultMetric="celkem"
        segments={[{ value: '12', label: '12M', ariaLabel: 'Posledních 12 měsíců' }, { value: '24', label: '24M', ariaLabel: 'Posledních 24 měsíců' }]}
        segmentValue={String(months)}
        onSegmentChange={(value) => onMonthsChange(value === '24' ? 24 : 12)}
        segmentLabel="Zobrazené období"
        compareLabel="Předchozí období"
        compareSeriesLabel="Předchozí období"
        breakdowns={[
          { title: 'Český trh', column: 'Součet', rows: marketRows('cz') },
          { title: 'Slovenský trh', column: 'Součet', rows: marketRows('sk') },
        ]}
        ariaLabel="Rozpad příjmů Affilmasteru"
      />
    </div>
  );
}

function TabVypis({ series }: { series: IncomeSeries }) {
  const [granularity, setGranularity] = useState<'month' | 'year'>('month');
  const [detailYm, setDetailYm] = useState<string | null>(null);
  const rows = useMemo(() => {
    if (granularity === 'month') return [...series.rows].reverse();
    const byYear = new Map<string, IncomeSeriesRow>();
    for (const r of series.rows) {
      const y = r.period.slice(0, 4);
      const acc = byYear.get(y);
      if (!acc) { byYear.set(y, { ...r, period: y }); continue; }
      acc.czk += r.czk; acc.eur += r.eur; acc.totalCzk += r.totalCzk; acc.fixedCzk += r.fixedCzk; acc.feesCzk += r.feesCzk;
      acc.partial = acc.partial || r.partial;
    }
    return [...byYear.values()].reverse();
  }, [series, granularity]);
  const tot = rows.reduce((a, r) => ({ czk: a.czk + r.czk, eur: a.eur + r.eur, total: a.total + r.totalCzk }), { czk: 0, eur: 0, total: 0 });
  const periodLabel = (p: string) => (granularity === 'year' ? p : ymLabel(p));
  const isMonth = granularity === 'month';

  return (
    <div className="tab-content am-prijmy-list">
      <ServiceBar
        variant="seg"
        selects={[
          { label: 'Zobrazení', value: granularity, onChange: (v) => setGranularity(v as 'month' | 'year'), options: [{ value: 'month', label: 'Měsíčně' }, { value: 'year', label: 'Ročně' }] },
        ]}
        right={<span className="am-prijmy-list-meta">{`${rows.length} období · € přepočteno pevným kurzem ${series.rate.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} Kč`}</span>}
      />
      <ArcTableFrame className="prototype-panel aff-finance-history-shell am-prijmy-history-shell" label="Příjmy po obdobích">
        <div className="table-responsive-mobile">
          <table className="table table-hover align-middle aff-list-table aff-finance-history-table am-prijmy-history-table sticky-thead mb-0" aria-label="Příjmy po obdobích">
            <thead>
              <tr>
                <th className="ps-3">Období</th>
                <th className="text-end">Příjmy CZK</th>
                <th className="text-end">Příjmy EUR</th>
                <th className="text-end">Celkem CZK</th>
                <th className="text-end pe-3" style={{ width: 40 }} aria-label="Detail"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={5} className="text-center py-4 text-muted">Žádná data.</td></tr>
                : rows.map((r) => (
                  <tr
                    key={r.period}
                    className={`${isMonth ? 'aff-row' : ''}${r.partial ? ' is-current' : ''}`.trim() || undefined}
                    onClick={isMonth ? () => setDetailYm(r.period) : undefined}
                  >
                    <td className="ps-3">
                      {periodLabel(r.period)}
                      {r.partial && <span className="am-prijmy-current-dot" title="Aktuální období – běží">●</span>}
                    </td>
                    <td className="text-end">{fmtCZK(r.czk)}</td>
                    <td className="text-end">{fmtEUR(r.eur)}</td>
                    <td className="text-end">{fmtCZK(r.totalCzk)}</td>
                    <td className="prototype-row-arrow pe-3" aria-hidden="true">{isMonth ? <span className="feather-icon"><i data-feather="chevron-right" /></span> : null}</td>
                  </tr>
                ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td className="ps-3">Souhrn</td>
                  <td className="text-end">{fmtCZK(tot.czk)}</td>
                  <td className="text-end">{fmtEUR(tot.eur)}</td>
                  <td className="text-end">{fmtCZK(tot.total)}</td>
                  <td className="pe-3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </ArcTableFrame>
      <div className="am-prijmy-list-note">
        <span className="am-prijmy-current-dot">●</span> aktuální období (běží) · Příjmy tvoří fixní položky z dealů a provize AM odhadnuté z dat PaP.
      </div>
      {detailYm && <MonthPrijmyModal ym={detailYm} onClose={() => setDetailYm(null)} />}
    </div>
  );
}

function MonthPrijmyModal({ ym, onClose }: { ym: string; onClose: () => void }) {
  const [data, setData] = useState<SettlementForecast | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getAffilmasterPrijmyMonth(ym)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení detailu selhalo'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ym]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const partners = (data?.partners ?? []).filter((p) => p.hasDeals).sort((a, b) => b.totalCzk - a.totalCzk);
  return (
    <ArcDialog
      title={`Příjmy · ${ymLabel(ym)}`}
      description={`${partners.length} klientů s dealy · fixní položky a provize AM po klientech`}
      onClose={onClose}
      size="wide"
      className="aff-month-detail-modal"
      bodyClassName="aff-month-detail-body"
    >
          {err && <div className="alert alert-danger mb-0">{err}</div>}
          {!err && loading && <div className="text-muted"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>}
          {!err && !loading && data && (
            <>
              <div className="kmonth-kpi-grid mb-3">
                <div className="kmonth-kpi"><div className="kmonth-kpi-label">Celkem Kč</div><div className="kmonth-kpi-value">{fmtCZK(data.income.totalCzk)}</div></div>
                <div className="kmonth-kpi"><div className="kmonth-kpi-label">Fixní</div><div className="kmonth-kpi-value">{fmtCZK(data.fixed.totalCzk)}</div></div>
                <div className="kmonth-kpi"><div className="kmonth-kpi-label">Provize RS</div><div className="kmonth-kpi-value">{fmtCZK(data.fees.rsCzk + data.fees.rsEur * data.rate)}</div></div>
                <div className="kmonth-kpi"><div className="kmonth-kpi-label">Provize CPA</div><div className="kmonth-kpi-value">{fmtCZK(data.fees.cpaCzk + data.fees.cpaEur * data.rate)}</div></div>
                <div className="kmonth-kpi"><div className="kmonth-kpi-label">Registrace</div><div className="kmonth-kpi-value">{fmtCZK(data.fees.regCzk + data.fees.regEur * data.rate)}</div></div>
                <div className="kmonth-kpi"><div className="kmonth-kpi-label">Pevný kurz</div><div className="kmonth-kpi-value">{data.rate.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} Kč</div></div>
              </div>
              <ArcTableFrame className="aff-table-wrap aff-month-detail-scroll" label="Příjmy po klientech" sticky="container">
                <div className="table-responsive-mobile">
                  <table className="table table-hover align-middle aff-list-table aff-month-detail-table mb-0" aria-label="Příjmy po klientech">
                    <thead>
                      <tr>
                        <th className="ps-3">Klient</th>
                        <th>Fixní položky</th>
                        <th className="text-end">Fixní</th>
                        <th className="text-end">Provize RS</th>
                        <th className="text-end">Provize CPA</th>
                        <th className="text-end">Registrace</th>
                        <th className="text-end pe-3">Celkem Kč</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partners.length === 0
                        ? <tr><td colSpan={7} className="text-center py-4 text-muted">Žádný klient s dealy v tomto měsíci.</td></tr>
                        : partners.map((p) => (
                          <tr key={p.partnerId}>
                            <td className="ps-3"><span className={`market-flag market-flag-${p.market} me-2`} style={{ fontSize: '0.65rem' }}>{p.market.toUpperCase()}</span>{p.name}</td>
                            <td className="text-muted small">{p.fixedItems.map((f) => f.title).join(' · ') || '-'}</td>
                            <td className="text-end">{fmtMoney(p.fixed, p.fixedCurrency)}</td>
                            <td className="text-end">{fmtMoney(p.rs, p.feeCurrency)}</td>
                            <td className="text-end">{fmtMoney(p.cpa, p.feeCurrency)}</td>
                            <td className="text-end">{fmtMoney(p.reg, p.feeCurrency)}</td>
                            <td className="text-end pe-3">{fmtCZK(p.totalCzk)}</td>
                          </tr>
                        ))}
                    </tbody>
                    {partners.length > 0 && (
                      <tfoot>
                        <tr style={{ fontWeight: 600, borderTop: '2px solid #e2e8f0' }}>
                          <td className="ps-3" colSpan={2}>Souhrn</td>
                          <td className="text-end">{fmtCZK(data.fixed.totalCzk)}</td>
                          <td className="text-end">{fmtCZK(data.fees.rsCzk + data.fees.rsEur * data.rate)}</td>
                          <td className="text-end">{fmtCZK(data.fees.cpaCzk + data.fees.cpaEur * data.rate)}</td>
                          <td className="text-end">{fmtCZK(data.fees.regCzk + data.fees.regEur * data.rate)}</td>
                          <td className="text-end pe-3">{fmtCZK(data.income.totalCzk)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </ArcTableFrame>
            </>
          )}
    </ArcDialog>
  );
}
